import React, { useEffect } from "react";
import Layout from "../../../components/Layout";
import { Link, useLocation } from "react-router-dom";
import { FilterContextProvider } from "../../../contexts/FilterContext";
import Chip from "../../../components/Chip";
import DataTable from "../../../components/DataTable";
import useData from "../../../hooks/useData";
import { DownloadIcon, EyeOpen, PrintIcon } from "../../../assets/icons/Icons";
import SearchFilter from "../../../components/filters/SearchFilter";
import DateRangeFilter from "../../../components/filters/DateRangeFilter";
import { formatDate } from "../../../utils/formatDate";
import { useUserContext } from "../../../contexts/UserContext";
import ResetFilters from "../../../components/filters/ResetFilters";
// import ExportDataButton from "../../../components/ExportDataButton";
import { useTranslation } from "react-i18next";
import CustomTooltip from "../../../components/CustomTooltip";
import SelectFilter from "../../../components/filters/SelectFilter";
import usePrint from "../../../hooks/usePrint";
import { CentsToDollars } from "../../../utils/formatPrice";
import OfficesFilter from "../../../components/filters/OfficesFilter";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import { MOD_SELL_ORDER } from "../../../contstants";
import CombineCardModal from "../../../components/combine-cards/combinecardModal";
import FilterCombineOrders from "../../../api/sell-orders/filterCombineOrders";
import ViewCombineOrder from "../../../components/sell-order/ViewCombineOrder";
import { getCombineCardReceipt } from "../../../api/sell-orders/getCombineCardReceipt";
import ExportDataButton from "../../../components/ExportDataButton";
import { downloadFile } from "../../../api/downloadFile";
import { ExportCombineCards } from "../../../api/sell-orders/exportCombineCards";

function Index(props) {
  const { t } = useTranslation();

  const {
    data,
    errorMessage,
    loadingData,
    pageCount,
    selectedItem,
    setSelectedItem,
    showViewItem,
    setShowViewItem,
    showCreateItem,
    setShowCreateItem,
  } = useData(FilterCombineOrders);

  const { can: userCan, hasPermission, hasDynamicPermissions } =
    useUserContext();

  const canCombine = hasDynamicPermissions()
    ? hasPermission("sell_orders", "write")
    : userCan(MOD_SELL_ORDER, "write");

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();

  useEffect(() => {
    setPageTitle(t("Card Issuing"));
    setBreadcrumbs([t("Card Issuing"), t("Combine Cards")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  const { handlePrint: handlePrintReceipt } = usePrint(getCombineCardReceipt);

  // Export CSV
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryParams = {};
  searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });

  const handleExportCSV = async () => {
    try {
      const response = await FilterCombineOrders(-1, queryParams);
      const ids = response.data.map((item) => item.id);
      await downloadFile(
        ExportCombineCards,
        { ids: ids },
        "combine_cards.csv"
      );
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  return (
    <Layout title={t("Combine Cards")} breadcrumbs={t("Card Issuing")}>
      {showCreateItem && canCombine && (
        <CombineCardModal onClose={() => setShowCreateItem(false)} />
      )}

      {showViewItem && (
        <ViewCombineOrder
          order={selectedItem}
          onClose={() => setShowViewItem(false)}
        />
      )}

      <DataTable
        filtersTop={
          <>
            <FilterContextProvider name="search_in_fields">
              <SelectFilter
                options={[
                  {
                    value: "user",
                    label: t("By User"),
                  },
                ]}
                defaultValue="user"
                placeholder={<div>{t("Search by")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider name="s">
              <SearchFilter placeholderText={t("Search")} />
            </FilterContextProvider>
          </>
        }
        filtersBottom={
          <>
            <FilterContextProvider name="date">
              <DateRangeFilter />
            </FilterContextProvider>
            <FilterContextProvider name="office_id">
              <OfficesFilter />
            </FilterContextProvider>
            <FilterContextProvider name="status">
              <SelectFilter
                options={[
                  {
                    value: "all",
                    label: t("Select Status"),
                  },
                  {
                    value: "completed",
                    label: t("Completed"),
                  },
                  {
                    value: "failed",
                    label: t("Failed"),
                  },
                ]}
                defaultValue="all"
                placeholder={<div>{t("Filter by Status")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider>
              <ResetFilters />
            </FilterContextProvider>
          </>
        }
        buttons={
          <>
            {canCombine && (
              <button
                className="btn btn-primary btn-medium"
                onClick={(e) => setShowCreateItem(true)}
              >
                {t("Combine")}
              </button>
            )}
            <ExportDataButton
              className="btn btn-secondary btn-medium"
              onClick={handleExportCSV}
              label={t("Export CSV")}
              disabled={!data.data?.length}
            />
          </>
        }
        columns={[
          { header: t("Order Number"), accessor: "id" },
          { header: t("Date"), accessor: "created_at" },
          { header: t("Order Type"), accessor: "type" },
          { header: t("Order Status"), accessor: "status" },
          { header: t("User"), accessor: "created_by_name" },
          { header: t("User ID"), accessor: "created_by" },
          {
            header: t("Total Combined Amount"),
            accessor: "total_source_amount",
          },
          { header: t("Office"), accessor: "office_name" },
          { header: t("Actions"), accessor: "actions" },
        ]}
        rows={data.data || []}
        pageCount={pageCount}
        loadingData={loadingData}
        error={errorMessage}
        actions={(item) => (
          <div className="d-flex align-items-center">
            {item.status === "completed" && (
              <>
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePrintReceipt({
                      requestParams: { id: item.id },
                      name: `combine-order-${item.id}`,
                      width: "",
                      height: "",
                      download: false,
                    });
                  }}
                >
                  <CustomTooltip
                    icon={<PrintIcon />}
                    message={t("Print Receipt")}
                  />
                </Link>
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePrintReceipt({
                      requestParams: { id: item.id },
                      name: `combine-order-${item.id}`,
                      width: "",
                      height: "",
                      download: true,
                    });
                  }}
                >
                  <CustomTooltip
                    icon={<DownloadIcon />}
                    message={t("Download Receipt")}
                  />
                </Link>
              </>
            )}
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedItem(item);
                setShowViewItem(true);
              }}
            >
              <CustomTooltip icon={<EyeOpen />} message={t("View")} />
            </Link>
          </div>
        )}
        customOutputs={(columnName, value) => {
          switch (columnName) {
            case "status":
              let status = "failed";
              if (
                value === "submitted" ||
                value === "success" ||
                value === "completed"
              ) {
                status = "active";
              }
              return (
                <Chip
                  state={status}
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  showAsTooltop
                />
              );
            case "created_at":
              return formatDate(value);
            case "total_source_amount":
              return <p className="text-right">{CentsToDollars(value)}</p>;
            case "type":
              return <p className="text-uppercase">{value}</p>;
            default:
              return value;
          }
        }}
      />
    </Layout>
  );
}

export default Index;
